import { FiMessageSquare, FiUsers, FiHash } from 'react-icons/fi';
import { BsPinAngle, BsPinAngleFill } from 'react-icons/bs';
import { useChat } from '../../contexts/ChatContext';
import { useToast } from '../common/Toast';
import { relativeTime } from '../../utils/chatTime';
import { cn } from '../../utils/helpers';

const TYPE_ICON = {
  dm: FiMessageSquare,
  group: FiUsers,
  channel: FiHash,
};

// A tinted tile per conversation type, drawn from the site palette, so the list
// is scannable by shape and colour before any name is read.
const TYPE_TILE = {
  dm: 'bg-highlight text-primary/70',
  group: 'bg-neutral-200 text-secondary',
  channel: 'bg-accent/30 text-primary/70',
};

export default function ConversationList() {
  const { conversations, openConversation, toggleConversationPin } = useChat();
  const toast = useToast();

  if (conversations.length === 0) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center px-8 text-center bg-neutral-50">
        <span className="w-14 h-14 rounded-xl bg-highlight text-primary/60 flex items-center justify-center mb-3">
          <FiMessageSquare className="w-7 h-7" />
        </span>
        <p className="text-sm text-primary/70">No conversations yet.</p>
        <p className="text-xs text-primary/40 mt-1">Use the pencil icon above to start one.</p>
      </div>
    );
  }

  return (
    <ul className="flex-1 overflow-y-auto bg-neutral-50 divide-y divide-neutral-300">
      {conversations.map((conversation, index) => {
        const Icon = TYPE_ICON[conversation.type] || FiMessageSquare;
        const hasUnread = conversation.unreadCount > 0;

        // Marks where the pinned block ends, so the ordering reads as
        // deliberate rather than as a list that sorted itself oddly.
        const endsPinnedBlock =
          conversation.isPinned && !conversations[index + 1]?.isPinned &&
          index + 1 < conversations.length;

        return (
          <li
            key={conversation.id}
            className={cn(
              // Hover lives on the row, not the inner button, so the name area
              // and the pin column highlight together.
              'group relative flex items-stretch transition-colors',
              // An unread conversation carries a wash and an accent rail, so the
              // eye lands on it before reading a single name.
              hasUnread ? 'bg-highlight/40 hover:bg-highlight/70' : 'bg-white hover:bg-highlight/40',
              endsPinnedBlock && 'border-b-2 border-neutral-300'
            )}
          >
            {hasUnread && (
              <span className="absolute inset-y-0 left-0 w-[3px] bg-accent" aria-hidden="true" />
            )}

            <button
              onClick={() => openConversation(conversation.id)}
              className="flex-1 min-w-0 text-left pl-3 pr-2 py-3 flex gap-3 items-center"
            >
              <span
                className={cn(
                  'w-9 h-9 shrink-0 rounded-[10px] flex items-center justify-center',
                  TYPE_TILE[conversation.type] || TYPE_TILE.dm
                )}
              >
                <Icon className="w-4 h-4" />
              </span>

              <div className="flex-1 min-w-0">
                <div className="flex items-baseline gap-2">
                  {conversation.isPinned && (
                    <BsPinAngleFill className="w-3 h-3 shrink-0 text-secondary self-center" />
                  )}
                  <span
                    className={cn(
                      'truncate text-sm',
                      hasUnread ? 'font-semibold text-primary' : 'text-primary/80'
                    )}
                  >
                    {conversation.name}
                  </span>
                  <span className="ml-auto text-[11px] text-primary/40 shrink-0">
                    {relativeTime(conversation.lastMessageAt)}
                  </span>
                </div>

                {conversation.participantCount > 2 && (
                  <p className="text-xs text-primary/40 mt-0.5">
                    {conversation.participantCount} members
                  </p>
                )}
              </div>

              {hasUnread && (
                <span
                  className={cn(
                    'shrink-0 min-w-[20px] h-5 px-1.5 rounded-full text-[11px] font-semibold',
                    'flex items-center justify-center shadow-sm',
                    // Mentions are visually distinct from ordinary unread, the
                    // same way Slack separates them — being named matters more.
                    conversation.unreadMentions > 0
                      ? 'bg-alert text-white'
                      : 'bg-accent text-primary'
                  )}
                >
                  {conversation.unreadCount > 99 ? '99+' : conversation.unreadCount}
                </span>
              )}
            </button>

            {/* Its own column rather than absolutely positioned over the row.
                Floating it overlapped the timestamp on short rows, and the
                reserved width also stops the row reflowing on hover. */}
            <div className="shrink-0 w-9 flex items-center justify-center pr-1">
              <button
                onClick={async () => {
                  const result = await toggleConversationPin(conversation);
                  if (!result.ok) toast.error(result.message);
                }}
                className={cn(
                  'p-1.5 rounded-full transition-all hover:bg-primary/10',
                  conversation.isPinned
                    ? 'text-secondary opacity-100'
                    : 'text-primary/30 hover:text-primary opacity-0 group-hover:opacity-100 focus:opacity-100'
                )}
                aria-label={conversation.isPinned ? 'Unpin conversation' : 'Pin conversation'}
                title={conversation.isPinned ? 'Unpin from top' : 'Pin to top'}
              >
                {conversation.isPinned
                  ? <BsPinAngleFill className="w-3.5 h-3.5" />
                  : <BsPinAngle className="w-3.5 h-3.5" />}
              </button>
            </div>
          </li>
        );
      })}
    </ul>
  );
}
